/**
 * Spec 199 / 201 — typed client for the Google AI Overview search-record repair
 * route.
 *
 * Google AI Overview captures never recorded whether a web search ran, so every
 * one of them reads "status unknown". The repair reads only data GEO Observatory has
 * already stored (no DataForSEO re-query, no engine call, no cost) and stamps
 * the status the stored capture data proves: `invoked` where an AI Overview was
 * returned, `ran_no_answer` where a search ran and no AI Overview surfaced.
 * Responses whose case cannot be established are left `unknown` untouched.
 *
 * Spec 201 — the run is TWO phases, each driven request-by-request from here:
 *
 *   1. repair  — keyset cursor over responses (`after_id`, bounded `limit`),
 *                writing the missing search record. This phase alone leaves the
 *                screen unchanged: the citation figures are read from a
 *                different, already-computed place.
 *   2. rescore — keyset cursor over the affected score periods
 *                (`after_period` + `after_brand_id`, bounded `period_limit`),
 *                recomputing each day's visibility bucket from the corrected
 *                records so the history reads correctly, not just today.
 *
 * Phase 2 reports two counts, not one: buckets it walked (`periods_rescored`)
 * and score rows it actually wrote (`score_rows_written`). A day whose analyses
 * never completed is walked and writes nothing, so the pair is what tells a
 * clean run apart from one that changed nothing at all.
 *
 * Phase 1 stopping at the records was exactly the defect 201 fixes: on staging
 * the stored records were already right and the screen still read "Cited as a
 * source · Not evaluable". Both phases are bounded per request, which makes a
 * timeout much less likely — it cannot make one impossible — and a run that
 * stops early is safe to re-run: both phases are idempotent and resume from the
 * start of their own cursor, and phase 2's candidate set is derived from the
 * *current* data, so a later-corrected row is automatically back in scope.
 *
 *   POST /api/v1/admin/backfill/aio-search-status
 *     repair:  { brand_id, after_id?, limit? }
 *     rescore: { brand_id, phase: 'rescore', after_period?, after_brand_id?, period_limit? }
 *
 * Admin-gated and brand-access scoped server-side; idempotent.
 */
import { apiFetch } from '../lib/api.js';

/** Which half of the run a request/response belongs to. */
export type AioSearchStatusPhase = 'repair' | 'rescore';

/** One repair-phase batch: keyset over responses via `last_id`. */
export interface AioSearchStatusBatchResult {
  phase: 'repair';
  /** Rows examined in this batch. */
  scanned: number;
  /** Rows stamped `invoked` (an AI Overview was returned). */
  updated_invoked: number;
  /** Rows stamped `ran_no_answer` (a search ran, no AI Overview surfaced). */
  updated_ran_no_answer: number;
  /** Rows whose case the stored data cannot prove — left `unknown`. */
  skipped_unprovable: number;
  done: boolean;
  /**
   * Keyset cursor to echo back on the next call. Null when this batch scanned
   * nothing. This is what makes the loop terminate: rows the repair cannot prove
   * are deliberately left `unknown`, so without a cursor every call would
   * re-scan them and `done` would never arrive on a large brand.
   */
  last_id: string | null;
  /** Rows still at `unknown` in scope, INCLUDING ones deliberately left alone. */
  remaining: number;
  /** Ids of the brands actually touched by this batch. */
  brands: string[];
  /**
   * Phase-2 counters, honestly zero in this phase: it writes no scores. They are
   * PRESENT rather than omitted because `undefined + n` is `NaN` — every field
   * the client accumulates across both loops has to be numeric in both
   * responses, or one bad batch silently poisons the total in the toast.
   */
  periods_rescored: number;
  periods_failed: number;
  score_rows_written: number;
  /** Present only once the repair phase is `done` — the handoff to phase 2. */
  next_phase?: AioSearchStatusPhase;
}

/**
 * One rescore-phase batch: keyset over score periods via
 * `(last_period, last_brand_id)`.
 *
 * The phase-1 counters come back honestly zeroed rather than dropped, so the
 * client can accumulate them unconditionally across both loops.
 */
export interface AioSearchStatusRescoreBatchResult {
  phase: 'rescore';
  /** Score buckets recomputed by THIS request. */
  periods_rescored: number;
  /** Buckets this request tried to recompute and could not. */
  periods_failed: number;
  /**
   * Score rows actually written by this request. This is NOT a restatement of
   * `periods_rescored`, which counts buckets ATTEMPTED: the aggregation only
   * sees analyses that completed, so a day whose analyses never did aggregates
   * to nothing, writes nothing, and still counts as rescored. Without this
   * second number, "40 days updated, nothing written" is indistinguishable from
   * a clean run — the exact class of success-shaped failure this spec exists to
   * remove.
   */
  score_rows_written: number;
  /** `YYYY-MM-DD` cursor for the next request, or null when nothing advanced. */
  last_period: string | null;
  last_brand_id: string | null;
  done: boolean;
  /** Buckets still queued after this request's cursor. */
  periods_remaining: number;
  /** The request hit its own work budget and returned early (`done` is false). */
  budget_stopped: boolean;
  /** Ids of the brands actually touched by this batch, as in phase 1. */
  brands: string[];
  // Phase-1 fields, zeroed by the server so accumulation needs no branching.
  scanned: number;
  updated_invoked: number;
  updated_ran_no_answer: number;
  skipped_unprovable: number;
  last_id: string | null;
  remaining: number;
}

/** One batch result from either phase, discriminated on `phase`. */
export type AioSearchStatusAnyBatchResult =
  | AioSearchStatusBatchResult
  | AioSearchStatusRescoreBatchResult;

/** Accumulated totals across the whole two-phase run. */
export interface AioSearchStatusRepairResult {
  scanned: number;
  updated_invoked: number;
  updated_ran_no_answer: number;
  skipped_unprovable: number;
  /** Score buckets recomputed in phase 2. */
  periods_rescored: number;
  /** Buckets phase 2 tried to recompute and could not. */
  periods_failed: number;
  /**
   * Score rows written across phase 2. Zero here with `periods_rescored` above
   * zero means the run walked days and changed nothing — report that, never
   * "figures updated".
   */
  score_rows_written: number;
  batches: number;
}

export interface AioSearchStatusProgress {
  /** Which half of the run is currently executing. */
  phase: AioSearchStatusPhase;
  /** Rows corrected so far across all repair batches (both statuses). */
  updated: number;
  /** Rows examined so far across all repair batches. */
  scanned: number;
  /** Live count of rows still awaiting examination after the repair cursor. */
  remaining: number;
  /** Score buckets recomputed so far across all rescore batches. */
  periodsRescored: number;
  /** Live count of score buckets still queued after the rescore cursor. */
  periodsRemaining: number;
}

/**
 * Run one repair-phase keyset batch. Throws Error on non-2xx (matching the
 * `aioCitations.ts` pattern).
 */
export async function backfillAioSearchStatusBatch(
  brandId: string,
  afterId?: string | null,
  limit?: number,
): Promise<AioSearchStatusBatchResult> {
  const body: Record<string, unknown> = { brand_id: brandId };
  if (afterId) body.after_id = afterId;
  if (limit) body.limit = limit;
  const res = await apiFetch(`/api/v1/admin/backfill/aio-search-status`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`AI Overview search-status repair failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: AioSearchStatusBatchResult };
  return json.data;
}

/**
 * Run one rescore-phase keyset batch. Same error-handling contract as the repair
 * batch above. The cursor is composite — `(period, brand_id)` — because a period
 * can span several brands in the multi-brand scope; the brand-scoped caller
 * still echoes back whatever brand half the server hands it.
 */
export async function backfillAioSearchStatusRescoreBatch(
  brandId: string,
  afterPeriod: string | null = null,
  afterBrandId: string | null = null,
  periodLimit?: number,
): Promise<AioSearchStatusRescoreBatchResult> {
  const body: Record<string, unknown> = { brand_id: brandId, phase: 'rescore' };
  if (afterPeriod) body.after_period = afterPeriod;
  if (afterBrandId) body.after_brand_id = afterBrandId;
  if (periodLimit) body.period_limit = periodLimit;
  const res = await apiFetch(`/api/v1/admin/backfill/aio-search-status`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`AI Overview search-status repair failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: AioSearchStatusRescoreBatchResult };
  return json.data;
}

/**
 * Hard caps on the number of requests each phase may make. Both are far beyond
 * any real brand; they exist so a server that ever returned done=false with no
 * forward progress cannot spin forever.
 *
 * Spec 201 (inherited from 198) — the two phases MUST NOT share a counter. A
 * large brand's repair could otherwise exhaust the shared budget and leave the
 * rescore truncated while the run still resolved, i.e. a failure shaped exactly
 * like success.
 */
const MAX_REPAIR_BATCHES = 20000;
const MAX_RESCORE_BATCHES = 20000;

/**
 * Drive both phases to completion: correct every search record, then recompute
 * every affected score period. `onProgress` fires after each batch (tagged with
 * the phase) so the UI can say which half is running. Resolves only once BOTH
 * phases report done, so the caller may treat resolution as "run finished".
 *
 * Ordering is a protocol invariant: phase 1 always runs first, because a period
 * rescored while some of its responses were still `unknown` would be passed by
 * the monotone cursor with a stale score left behind. Re-driving phase 2 from a
 * null cursor is the (cheap, idempotent) recovery.
 */
export async function runAioSearchStatusRepair(
  brandId: string,
  onProgress?: (p: AioSearchStatusProgress) => void,
  limit?: number,
  periodLimit?: number,
): Promise<AioSearchStatusRepairResult> {
  const totals: AioSearchStatusRepairResult = {
    scanned: 0,
    updated_invoked: 0,
    updated_ran_no_answer: 0,
    skipped_unprovable: 0,
    periods_rescored: 0,
    periods_failed: 0,
    score_rows_written: 0,
    batches: 0,
  };

  // ---- Phase 1: repair, keyed on `last_id` --------------------------------
  let cursor: string | null = null;
  for (let i = 0; i < MAX_REPAIR_BATCHES; i++) {
    const batch: AioSearchStatusBatchResult = await backfillAioSearchStatusBatch(
      brandId,
      cursor,
      limit,
    );
    // `?? 0` on every accumulation, in BOTH loops. A bundle served against a
    // server of a different vintage (either direction, mid-deploy) can be missing
    // a field, and `undefined + n` is `NaN` — which would surface as "NaN answers
    // corrected" in the toast rather than as an error anyone could act on.
    totals.scanned += batch.scanned ?? 0;
    totals.updated_invoked += batch.updated_invoked ?? 0;
    totals.updated_ran_no_answer += batch.updated_ran_no_answer ?? 0;
    totals.skipped_unprovable += batch.skipped_unprovable ?? 0;
    // Zero in this phase today. Accumulate anyway, so the two accumulation sites
    // stay identical if the server ever writes scores here.
    totals.periods_rescored += batch.periods_rescored ?? 0;
    totals.periods_failed += batch.periods_failed ?? 0;
    totals.score_rows_written += batch.score_rows_written ?? 0;
    totals.batches += 1;
    onProgress?.({
      phase: 'repair',
      updated: totals.updated_invoked + totals.updated_ran_no_answer,
      scanned: totals.scanned,
      remaining: batch.remaining ?? 0,
      periodsRescored: 0,
      periodsRemaining: 0,
    });
    if (batch.done) break;
    // No forward progress (defensive): stop rather than spin.
    if (batch.scanned === 0 || !batch.last_id || batch.last_id === cursor) break;
    cursor = batch.last_id;
  }

  // ---- Phase 2: rescore, keyed on `(last_period, last_brand_id)` ----------
  // Starts from a null cursor — the repair phase's cursor does not carry over.
  // `budget_stopped` is NOT a stop condition: the server already reports
  // `done: false` for a budget-stopped page, so following `done` is correct and
  // treating it as done would truncate the run.
  let periodCursor: string | null = null;
  let brandCursor: string | null = null;
  for (let j = 0; j < MAX_RESCORE_BATCHES; j++) {
    const batch: AioSearchStatusRescoreBatchResult = await backfillAioSearchStatusRescoreBatch(
      brandId,
      periodCursor,
      brandCursor,
      periodLimit,
    );
    totals.periods_rescored += batch.periods_rescored ?? 0;
    totals.periods_failed += batch.periods_failed ?? 0;
    totals.score_rows_written += batch.score_rows_written ?? 0;
    totals.batches += 1;
    onProgress?.({
      phase: 'rescore',
      updated: totals.updated_invoked + totals.updated_ran_no_answer,
      scanned: totals.scanned,
      remaining: 0,
      periodsRescored: totals.periods_rescored,
      periodsRemaining: batch.periods_remaining ?? 0,
    });
    if (batch.done) break;
    // No forward progress (defensive), mirroring the repair guard. The cursor is
    // composite, so a repeated period is only a stall when the brand half did
    // not advance either.
    if (!batch.last_period) break;
    if (batch.last_period === periodCursor && batch.last_brand_id === brandCursor) break;
    periodCursor = batch.last_period;
    brandCursor = batch.last_brand_id;
  }

  return totals;
}
